import { defineStore } from 'pinia';
import { uploadConfigService } from '@/services/uploadConfigService';

const FALLBACK_MAX_MB = 300;
const STORAGE_KEY = 'upload_config_max_mb';

interface UploadConfigState {
  maxMb: number;
  loaded: boolean;
  loading: Promise<void> | null;
}

/**
 * Mirrors the API's `/storage/upload-config` endpoint, which itself reads
 * from nginx-aligned config on the server (config/upload.php → UPLOAD_MAX_SIZE_MB).
 *
 * Used by Vue3Dropzone instances to display and enforce the same upload
 * limit the API actually accepts. Single source of truth: change nginx
 * + UPLOAD_MAX_SIZE_MB on the server, and every dropzone updates on next
 * page load.
 */
export const useUploadConfigStore = defineStore('uploadConfig', {
  state: (): UploadConfigState => {
    const cached = Number(localStorage.getItem(STORAGE_KEY));
    return {
      maxMb: Number.isFinite(cached) && cached > 0 ? cached : FALLBACK_MAX_MB,
      loaded: false,
      loading: null,
    };
  },

  actions: {
    async ensureLoaded(): Promise<void> {
      if (this.loaded) return;
      if (this.loading) return this.loading;

      this.loading = (async () => {
        try {
          const config = await uploadConfigService.fetch();
          if (config && Number.isFinite(config.max_upload_size_mb) && config.max_upload_size_mb > 0) {
            this.maxMb = config.max_upload_size_mb;
            localStorage.setItem(STORAGE_KEY, String(this.maxMb));
          }
          this.loaded = true;
        } catch (err) {
          // Keep using fallback / cached value if fetch fails — better than blocking uploads.
          console.warn('Failed to load upload config from API, using fallback:', err);
        } finally {
          this.loading = null;
        }
      })();

      return this.loading;
    },

    /**
     * Drop the cached limit so the next ensureLoaded() re-fetches.
     *
     * Needed on logout: `loaded` gates ensureLoaded(), and Pinia state lives
     * for the lifetime of the PAGE, not the session. Without this, signing out
     * and signing back in as a different company keeps the previous account's
     * upload limit until the browser is refreshed.
     */
    reset(): void {
      this.maxMb = FALLBACK_MAX_MB;
      this.loaded = false;
      this.loading = null;
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* storage unavailable — in-memory reset above is what matters */
      }
    },
  },
});
